import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import cookieBanner from "assets/images/cookiebanner.png";
import { useEffect } from "react";
import CookieConsent from "react-cookie-consent";
import { Link } from "react-router-dom";

declare global {
    interface Window {
        dataLayer: Record<string, any>[];
        gtag: (...args: any[]) => void;
    }
}

/** Marketing cookies that must not persist after opt-out */
const MARKETING_COOKIES = [
    "_fbp",
    "_fbc",
    "fr",
    "_gcl_au",
    "_gcl_aw",
    "_gcl_gs",
    "_ga",
    "_gid",
    "_gat",
];

const CookiesAccept = () => {
    const getCookie = (name: string) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(";").shift();
    };

    const setCookie = (name: string, value: string) => {
        document.cookie = `${name}=${value}; path=/; SameSite=Lax`;
    };

    const deleteCookie = (name: string) => {
        const hostname = window.location.hostname;
        const domains = [
            "",
            hostname,
            `.${hostname}`,
            hostname.replace(/^www\./, ""),
            `.${hostname.replace(/^www\./, "")}`,
        ];

        domains.forEach((domain) => {
            const domainPart = domain ? `; domain=${domain}` : "";
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
            document.cookie = `${name}=; Max-Age=0; path=/${domainPart}`;
        });
    };

    const clearMarketingCookies = () => {
        MARKETING_COOKIES.forEach(deleteCookie);

        // Also clear any Google Analytics cookies with measurement ID suffixes
        document.cookie.split(";").forEach((cookie) => {
            const name = cookie.split("=")[0]?.trim();
            if (
                name &&
                (name.startsWith("_ga_") ||
                    name.startsWith("_gid") ||
                    name.startsWith("_gat") ||
                    name.startsWith("_gcl_"))
            ) {
                deleteCookie(name);
            }
        });
    };

    const ensureGtag = () => {
        window.dataLayer = window.dataLayer || [];
        if (typeof window.gtag !== "function") {
            window.gtag = function gtag() {
                // eslint-disable-next-line prefer-rest-params
                window.dataLayer.push(arguments as unknown as Record<string, any>);
            };
        }
    };

    const setConsentDefault = (granted: boolean) => {
        ensureGtag();
        const value = granted ? "granted" : "denied";
        window.gtag("consent", "default", {
            ad_user_data: value,
            ad_personalization: value,
            ad_storage: value,
            analytics_storage: value,
            wait_for_update: 500,
        });
    };

    const updateConsent = (granted: boolean) => {
        ensureGtag();
        const value = granted ? "granted" : "denied";
        window.gtag("consent", "update", {
            ad_user_data: value,
            ad_personalization: value,
            ad_storage: value,
            analytics_storage: value,
        });
        window.dataLayer.push({ event: "consent_update" });
    };

    const loadGTM = () => {
        if (document.getElementById("gtm-script")) {
            return;
        }

        const googleTagManagerScript = document.createElement("script");
        googleTagManagerScript.id = "gtm-script";
        googleTagManagerScript.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-M8MFR3RS');
        `;
        googleTagManagerScript.async = true;
        document.head.appendChild(googleTagManagerScript);
    };

    const acceptGTM = () => {
        updateConsent(true);
        setCookie("cookie_consent", "true");
        loadGTM();
    };

    const declineGTM = () => {
        updateConsent(false);
        setCookie("cookie_consent", "false");
        clearMarketingCookies();
    };

    useEffect(() => {
        const hasConsent = getCookie("cookie_consent") === "true";

        // Always set Consent Mode defaults before any tags run
        setConsentDefault(hasConsent);

        if (hasConsent) {
            loadGTM();
        } else {
            // Remove leftover marketing cookies from prior sessions / ungated tags
            clearMarketingCookies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <CookieConsent
                location="bottom"
                buttonText="Accept all cookies"
                declineButtonText="Only essential"
                cookieName="cookie_consent"
                flipButtons={true}
                style={{
                    display: "flex",
                    alignItems: "center",
                    height: "286px",
                    width: "99%",
                    left: "10px",
                    backgroundImage: `url(${cookieBanner})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    borderRadius: "15px",
                    padding: "0 100px",
                }}
                buttonStyle={{
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "700",
                    fontFamily: "Univia Pro",
                    backgroundColor: "#3399FF",
                    borderRadius: "100px",
                    padding: "10px 45px",
                }}
                declineButtonStyle={{
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "700",
                    fontFamily: "Univia Pro",
                    backgroundColor: "#3399FF",
                    borderRadius: "100px",
                    padding: "10px 45px",
                }}
                expires={150}
                enableDeclineButton
                onAccept={() => {
                    acceptGTM();
                }}
                onDecline={() => {
                    declineGTM();
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", width: "340px" }}>
                    <Typography sx={{ fontSize: "30px", fontWeight: "700" }}>Cookie notice</Typography>
                    <Typography sx={{ fontWeight: "450", fontFamily: "Neue Haas Grotesk Display light", fontSize: "14px" }}>
                        This website employs cookies to improve user experience. If you continue on this website you will be providing your consent to our use of all cookies.
                    </Typography>

                    <Link to="/privacy-policy" style={{ color: "white", fontFamily: "Univia Pro", marginTop: "40px", fontWeight: "700", textDecoration: "underline", fontSize: "14px" }}>
                        Read our Privacy Policy here
                    </Link>
                </Box>
            </CookieConsent>
        </>
    );
};

export default CookiesAccept;

